/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { R } from 'retemplate';
import { isInt, compareBigInts, normalizeString, INSTANT_HASH_SEED } from './internal/core';
import { getDatePart, getDate, DatePart } from './internal/date';
import { getTimePart, getTime, TimePart } from './internal/time';
import { getInstantPart, getInstant, getDateOfInstant, getTimeOfInstant, InstantPart, truncateInstant } from './internal/instant';
import { instantDiff, instantPlus } from './internal/math';
import { TICKS } from './internal/symbols';
import { NANOS_OF_EPOCH } from './internal/types';
import { CALENDAR_DATETIME_EXTENDED } from './internal/patterns';
import { Components, TimeDelta, InstantUnit, TimeUnit, Temporal } from './types';
import { Zone, ZoneOffset } from './Zone';
import { Clock } from './Clock';
import { CivilDateTime } from './CivilDateTime';
import { CivilDate } from './CivilDate';
import { CivilTime } from './CivilTime';
import { OffsetDateTime } from './OffsetDateTime';
import { ZonedDateTime } from './ZonedDateTime';
import { Duration } from './Duration';
import { Comparable, Equatable, Equaler } from '@esfx/equatable';

const instantRegExp = new RegExp(R`^${CALENDAR_DATETIME_EXTENDED}Z$`);

export class Instant implements Temporal<Instant>, Equatable, Comparable {
    static readonly EPOCH = new Instant(0n);

    /* @internal */ private [TICKS]: NANOS_OF_EPOCH;

    constructor(ns: bigint) {
        this[TICKS] = ns as NANOS_OF_EPOCH;
    }

    get milliseconds() { return getInstantPart(this[TICKS], InstantPart.milliseconds); }
    get nanoseconds() { return getInstantPart(this[TICKS], InstantPart.nanoseconds); }

    plus(deltas: Components<TimeDelta> = {}) {
        const { hours = 0, minutes = 0, seconds = 0, milliseconds = 0, nanoseconds = 0 } = deltas;
        if (!isInt(hours)) throw new RangeError(`The value ${hours} is not valid for 'hours' as it is not an integer.`);
        if (!isInt(minutes)) throw new RangeError(`The value ${minutes} is not valid for 'minutes' as it is not an integer.`);
        if (!isInt(seconds)) throw new RangeError(`The value ${seconds} is not valid for 'seconds' as it is not an integer.`);
        if (!isInt(milliseconds)) throw new RangeError(`The value ${milliseconds} is not valid for 'milliseconds' as it is not an integer.`);
        if (!isInt(nanoseconds)) throw new RangeError(`The value ${nanoseconds} is not valid for 'nanoseconds' as it is not an integer.`);
        return new Instant(instantPlus(this[TICKS], hours, minutes, seconds, milliseconds, nanoseconds));
    }

    minus(deltas: Components<TimeDelta> = {}) {
        const { hours = 0, minutes = 0, seconds = 0, milliseconds = 0, nanoseconds = 0 } = deltas;
        if (!isInt(hours)) throw new RangeError(`The value ${hours} is not valid for 'hours' as it is not an integer.`);
        if (!isInt(minutes)) throw new RangeError(`The value ${minutes} is not valid for 'minutes' as it is not an integer.`);
        if (!isInt(seconds)) throw new RangeError(`The value ${seconds} is not valid for 'seconds' as it is not an integer.`);
        if (!isInt(milliseconds)) throw new RangeError(`The value ${milliseconds} is not valid for 'milliseconds' as it is not an integer.`);
        if (!isInt(nanoseconds)) throw new RangeError(`The value ${nanoseconds} is not valid for 'nanoseconds' as it is not an integer.`);
        return new Instant(instantPlus(this[TICKS], -hours, -minutes, -seconds, -milliseconds, -nanoseconds));
    }

    truncate(unit: TimeUnit) {
        return Instant.fromNanoseconds(truncateInstant(this[TICKS], unit));
    }

    with(components: Components<InstantUnit> = {}) {
        const { milliseconds = this.milliseconds, nanoseconds = this.nanoseconds } = components;
        return Instant.fromMilliseconds(milliseconds, nanoseconds);
    }

    withOffset(offset: ZoneOffset) {
        return OffsetDateTime.fromInstant(offset, this);
    }

    withZone(zone: Zone | string) {
        return new ZonedDateTime(this, zone);
    }

    until(other: Instant): Duration {
        if (!(other instanceof Instant)) throw new TypeError();
        return new Duration(instantDiff(this[TICKS], other[TICKS]));
    }

    since(other: Instant) {
        if (!(other instanceof Instant)) throw new TypeError();
        return other.until(this);
    }

    equals(other: Instant) {
        return other !== null
            && other !== undefined
            && this[TICKS] === other[TICKS];
    }

    compareTo(other: Instant): number {
        return compareBigInts(this[TICKS], other[TICKS]);
    }

    toCivilDateTime() {
        const { year, month, day } = getDatePart(getDateOfInstant(this[TICKS]), DatePart.all);
        const { hour, minute, second, millisecond, nanosecond } = getTimePart(getTimeOfInstant(this[TICKS]), TimePart.all);
        return new CivilDateTime(year, month, day, hour, minute, second, millisecond, nanosecond);
    }

    toCivilDate() {
        return CivilDate.fromDayOfEpoch(getDateOfInstant(this[TICKS]));
    }

    toCivilTime() {
        return CivilTime.fromNanoseconds(getTimeOfInstant(this[TICKS]));
    }

    toISOString() {
        return `${this.toCivilDateTime().toCalendarDateTimeString()}Z`;
    }

    toString() {
        return this.toISOString();
    }

    toJSON() {
        return this.toISOString();
    }

    static fromString(text: string) {
        const match = instantRegExp.exec(normalizeString(text));
        if (match) {
            const { year, month, day, hour, minute, second, millisecond = "", nanosecond = "" } = match.groups!;
            return new Instant(getInstant(
                getDate(+year, +month, +day),
                getTime(+hour, +minute, +second, +millisecond.padEnd(3, "0"), +nanosecond.padEnd(6, "0"))));
        }

        throw new SyntaxError(`Invalid instant string '${text}'`);
    }

    static fromMilliseconds(milliseconds: number, nanoseconds: number = 0) {
        if (!isInt(milliseconds)) throw new RangeError(`The value ${milliseconds} is not valid for the number of milliseconds.`);
        if (!isInt(nanoseconds)) throw new RangeError(`The value ${nanoseconds} is not valid for the number of nanoseconds.`);
        return new Instant(getTime(0, 0, 0, milliseconds, nanoseconds));
    }

    static fromNanoseconds(nanoseconds: bigint) {
        return new Instant(nanoseconds);
    }

    static now(clock: Clock) {
        return clock.currentInstant();
    }

    [Equatable.equals](other: unknown) {
        return other instanceof Instant
            && this.equals(other);
    }

    [Equatable.hash]() {
        let hc = INSTANT_HASH_SEED;
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this[TICKS]);
        return hc;
    }

    [Comparable.compareTo](other: unknown) {
        if (!(other instanceof Instant)) throw new TypeError();
        return this.compareTo(other);
    }
}

Object.defineProperty(Instant.prototype, Symbol.toStringTag, {
    writable: true,
    configurable: true,
    value: "Instant"
});
